import hubspot from '../config/hubspot.json';

export const accessEmailHref = 'mailto:enquiries@assethound.uk';

export const firmWorkTypes = [
  { value: 'formal_insolvency', label: 'Formal insolvency (e.g. CVLs)' },
  { value: 'restructuring', label: 'Restructuring (e.g. Administrations)' },
  {
    value: 'restructuring_advisory_work',
    label: 'Restructuring advisory work (e.g. IBRs, Option Reviews)',
  },
  {
    value: 'investigations_contentious',
    label: 'Investigations and contentious work',
  },
  { value: 'solvent_liquidations', label: 'Solvent liquidations (MVLs)' },
  { value: 'other', label: 'Other' },
] as const;

export const specificWorkTypes = firmWorkTypes.filter(
  (type) => type.value !== 'other',
);

export const teamSizes = [
  { value: '1-5', label: '1–5 people' },
  { value: '6-25', label: '6–25 people' },
  { value: '26-50', label: '26–50 people' },
  { value: '51-100', label: '51–100 people' },
  { value: '101-500', label: '101–500 people' },
  { value: '501-1000', label: '501–1,000 people' },
  { value: '1001+', label: '1,001+ people' },
  { value: 'not_sure', label: 'Not sure' },
] as const;

export interface AccessRequestValues {
  firstname: string;
  lastname: string;
  email: string;
  company: string;
  phone: string;
  teamSize: string;
  firmWorkType: string[];
  websiteTrap: string;
}

export const emptyAccessRequest: AccessRequestValues = {
  firstname: '',
  lastname: '',
  email: '',
  company: '',
  phone: '',
  teamSize: '',
  firmWorkType: [],
  websiteTrap: '',
};

export type AccessRequestErrors = Partial<
  Record<keyof AccessRequestValues, string>
>;

export const hubspotDestination = {
  portalId: hubspot.portalId,
  formId:
    (import.meta.env.VITE_HUBSPOT_FORM_ID as string | undefined)?.trim() ||
    hubspot.formId,
};

export function isAccessRequestConfigured(destination = hubspotDestination) {
  return (
    destination.portalId === '149167811' &&
    /^[a-f\d]{8}-(?:[a-f\d]{4}-){3}[a-f\d]{12}$/i.test(destination.formId)
  );
}

export const requestAccessHref =
  isAccessRequestConfigured() || import.meta.env.DEV
    ? '/request-access'
    : accessEmailHref;

export function validateAccessRequest(
  values: AccessRequestValues,
  step?: 1 | 2,
): AccessRequestErrors {
  const errors: AccessRequestErrors = {};
  if (step !== 2) {
    for (const [name, label] of [
      ['firstname', 'your first name'],
      ['lastname', 'your last name'],
      ['company', 'your company name'],
    ] as const) {
      if (!values[name].trim()) errors[name] = `Enter ${label}.`;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      errors.email = 'Enter a valid email address.';
    }
  }
  if (step !== 1) {
    if (!teamSizes.some((size) => size.value === values.teamSize)) {
      errors.teamSize = 'Select your team size, or choose Not sure.';
    }
    if (
      !values.firmWorkType.length ||
      !values.firmWorkType.every((value) =>
        firmWorkTypes.some((type) => type.value === value),
      )
    ) {
      errors.firmWorkType = 'Select at least one type of work.';
    }
  }
  return errors;
}

export function buildAccessRequestPayload(
  values: AccessRequestValues,
  pageUri: string,
) {
  if (Object.keys(validateAccessRequest(values)).length)
    throw new Error('invalid');
  if (values.websiteTrap) throw new Error('invalid');
  const fields = [
    { name: 'firstname', value: values.firstname.trim() },
    { name: 'lastname', value: values.lastname.trim() },
    { name: 'email', value: values.email.trim() },
    { name: 'company', value: values.company.trim() },
    { name: 'phone', value: values.phone.trim() },
    {
      name: 'marketing_team_size',
      value: values.teamSize,
    },
    {
      name: 'marketing_firm_work_type',
      value: [...new Set(values.firmWorkType)].join(';'),
    },
  ]
    .filter((field) => field.value)
    .map((field) => ({ objectTypeId: '0-1', ...field }));
  const uri = new URL(pageUri);
  return {
    fields,
    context: {
      pageUri: `${uri.origin}/request-access`,
      pageName: 'Asset Hound — Enquiry',
    },
  };
}

export async function submitAccessRequest(
  values: AccessRequestValues,
  pageUri: string,
  destination = hubspotDestination,
) {
  if (!isAccessRequestConfigured(destination)) throw new Error('unavailable');
  const body = JSON.stringify(buildAccessRequestPayload(values, pageUri));
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20_000);
  try {
    const response = await fetch(
      `https://api.hsforms.com/submissions/v3/integration/submit/${destination.portalId}/${destination.formId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        signal: controller.signal,
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
      },
    );
    if (!response.ok) throw new Error('not_confirmed');
  } finally {
    clearTimeout(timeout);
  }
}
